import {
  ArrowPathIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import PasswordInput from '../ui/PasswordInput';
import { apiService } from '../../services/api';

interface ImportMapping {
  csvColumn: string;
  dbField: string;
  transform?: string;
  required: boolean;
}

interface ImportConfig {
  ftpHost: string;
  ftpUsername: string;
  ftpPassword: string;
  ftpPath: string;
  scheduleEnabled: boolean;
  scheduleTime: string;
  autoImport: boolean;
  autoGenerateAiDescriptions: boolean;
  fieldMappings: ImportMapping[];
  lastImport?: string;
  totalImported?: number;
}

const CarsForSaleImportConfig: React.FC = () => {
  const [config, setConfig] = useState<ImportConfig>({
    ftpHost: '',
    ftpUsername: '',
    ftpPassword: '',
    ftpPath: '/incoming/',
    scheduleEnabled: false,
    scheduleTime: '03:00',
    autoImport: true,
    autoGenerateAiDescriptions: false,
    fieldMappings: [
      { csvColumn: 'VIN', dbField: 'vin', required: true },
      { csvColumn: 'StockNumber', dbField: 'stock_number', required: true },
      { csvColumn: 'ModelYear', dbField: 'year', required: true },
      { csvColumn: 'Make', dbField: 'make', required: true },
      { csvColumn: 'Model', dbField: 'model', required: true },
      { csvColumn: 'Trim', dbField: 'trim', required: false },
      { csvColumn: 'BodyStyle', dbField: 'body_style', required: false },
      { csvColumn: 'Mileage', dbField: 'mileage', required: false },
      { csvColumn: 'EngineDescription', dbField: 'engine', required: false },
      { csvColumn: 'FuelType', dbField: 'fuel_type', required: false },
      { csvColumn: 'Transmission', dbField: 'transmission', required: false },
      { csvColumn: 'ExteriorColor', dbField: 'exterior_color', required: false },
      { csvColumn: 'InteriorColor', dbField: 'interior_color', required: false },
      { csvColumn: 'Price', dbField: 'asking_price', transform: 'currency', required: true },
      {
        csvColumn: 'OptionText',
        dbField: 'features',
        transform: 'commaSeparated',
        required: false,
      },
      { csvColumn: 'Description', dbField: 'description', required: false },
      { csvColumn: 'images', dbField: 'images', transform: 'commaSeparated', required: false },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [showSamplePreview, setShowSamplePreview] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await apiService.get<ImportConfig>('/vehicles/import/carsforsale/config');
      if (data) {
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to fetch import config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      await apiService.post('/vehicles/import/carsforsale/config', config);
      toast.success('Import configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save config:', error);
      toast.error('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      setConnectionStatus('idle');

      const response = (await apiService.post('/vehicles/import/carsforsale/test-connection', {
        ftpHost: config.ftpHost,
        ftpUsername: config.ftpUsername,
        ftpPassword: config.ftpPassword,
        ftpPath: config.ftpPath,
      })) as any;

      if (response.success) {
        setConnectionStatus('success');
        toast.success('FTP connection successful!');
      } else {
        setConnectionStatus('error');
        toast.error('FTP connection failed. Please check credentials.');
      }
    } catch (error) {
      setConnectionStatus('error');
      toast.error('Failed to test connection. Please verify FTP settings.');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleManualImport = async () => {
    // Trigger file input click
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Importing vehicles...');

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      const backendUrl = apiService.getBaseUrl();
      const response = await fetch(
        `${backendUrl}/api/v1/vehicles/import/carsforsale/manual-import`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const result = await response.json();

      toast.dismiss();
      if (result.success) {
        const generatedDescriptions = Number(result.ai_descriptions_generated || 0);
        const successMessage =
          generatedDescriptions > 0
            ? `Successfully imported ${result.imported} vehicles and generated ${generatedDescriptions} AI descriptions.`
            : `Successfully imported ${result.imported} vehicles.`;

        toast.success(successMessage);
        setConfig((prev) => ({
          ...prev,
          lastImport: new Date().toISOString(),
          totalImported: (prev.totalImported || 0) + result.imported,
        }));

        // Refresh config to get updated totals
        await fetchConfig();
      } else {
        toast.error('Import completed with errors. Check console for details.');
        console.error('Import errors:', result.errors);
      }
    } catch (error) {
      toast.dismiss();
      console.error('Import failed:', error);
      toast.error('Failed to import vehicles. Please check the file format.');
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePreviewSample = async () => {
    try {
      setLoading(true);
      const data = (await apiService.get('/vehicles/import/carsforsale/preview')) as any[];
      setSampleData(data.slice(0, 5)); // Show first 5 rows
      setShowSamplePreview(true);
      toast.success('Sample data loaded successfully!');
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load sample data. Please check FTP connection.');
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (index: number, field: keyof ImportMapping, value: any) => {
    const updatedMappings = [...config.fieldMappings];
    updatedMappings[index] = { ...updatedMappings[index], [field]: value };
    setConfig({ ...config, fieldMappings: updatedMappings });
  };

  return (
    <div className="space-y-6">
      {/* Hidden file input for CSV upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <CloudArrowUpIcon className="w-6 h-6 mr-2 text-blue-600" />
              CarsForSale.com FTP Import Configuration
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              CarsForSale.com pushes a CSV to your staging FTP server daily (~2am). Configure the
              connection below to fetch and import that file automatically.
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {config.lastImport && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Last Import</p>
                <p className="font-medium">{new Date(config.lastImport).toLocaleString()}</p>
                <p className="text-xs text-green-600">{config.totalImported || 0} vehicles total</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FTP Connection Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <FolderIcon className="w-5 h-5 mr-2 text-gray-600" />
          FTP Connection Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">FTP Host</label>
            <input
              type="text"
              value={config.ftpHost}
              onChange={(e) => setConfig({ ...config, ftpHost: e.target.value })}
              placeholder="ftp.staging.yourdomain.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">FTP Username</label>
            <input
              type="text"
              value={config.ftpUsername}
              onChange={(e) => setConfig({ ...config, ftpUsername: e.target.value })}
              placeholder="username"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">FTP Password</label>
            <PasswordInput
              value={config.ftpPassword}
              onChange={(e) => setConfig({ ...config, ftpPassword: e.target.value })}
              placeholder="password"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">FTP Path</label>
            <input
              type="text"
              value={config.ftpPath}
              onChange={(e) => setConfig({ ...config, ftpPath: e.target.value })}
              placeholder="/incoming/"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleTestConnection}
              disabled={
                testingConnection || !config.ftpHost || !config.ftpUsername || !config.ftpPassword
              }
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {testingConnection ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Cog6ToothIcon className="w-5 h-5 mr-2" />
                  Test Connection
                </>
              )}
            </button>
          </div>
        </div>

        {connectionStatus !== 'idle' && (
          <div
            className={`mt-4 p-3 rounded-md flex items-center ${
              connectionStatus === 'success'
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
            {connectionStatus === 'success' ? (
              <>
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                FTP connection successful! Ready to import.
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                Connection failed. Please verify credentials and try again.
              </>
            )}
          </div>
        )}
      </div>

      {/* Schedule Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Import Schedule</h3>

        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="scheduleEnabled"
              checked={config.scheduleEnabled}
              onChange={(e) => setConfig({ ...config, scheduleEnabled: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="scheduleEnabled" className="ml-2 block text-sm text-gray-900">
              Enable automatic daily import
            </label>
          </div>

          {config.scheduleEnabled && (
            <div className="ml-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Import Time (Server Time)
              </label>
              <input
                type="time"
                value={config.scheduleTime}
                onChange={(e) => setConfig({ ...config, scheduleTime: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Recommended: 3:00 AM (after CarsForSale 2am FTP push)
              </p>
            </div>
          )}

          <div className="flex items-center">
            <input
              type="checkbox"
              id="autoImport"
              checked={config.autoImport}
              onChange={(e) => setConfig({ ...config, autoImport: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="autoImport" className="ml-2 block text-sm text-gray-900">
              Auto-import new files when detected
            </label>
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="autoGenerateAiDescriptions"
              checked={config.autoGenerateAiDescriptions}
              onChange={(e) =>
                setConfig({ ...config, autoGenerateAiDescriptions: e.target.checked })
              }
              className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <div className="ml-2">
              <label htmlFor="autoGenerateAiDescriptions" className="block text-sm text-gray-900">
                Auto-generate AI descriptions after import
              </label>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, new and updated CarsForSale vehicles will receive fresh AI
                descriptions after each import completes.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Field Mappings */}
      <div className="bg-white shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <DocumentTextIcon className="w-5 h-5 mr-2 text-gray-600" />
          CSV Field Mappings
        </h3>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  CSV Column
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Maps To
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Transform
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Required
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {config.fieldMappings.map((mapping, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={mapping.csvColumn}
                      onChange={(e) => updateMapping(index, 'csvColumn', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={mapping.dbField}
                      onChange={(e) => updateMapping(index, 'dbField', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={mapping.transform || ''}
                      onChange={(e) =>
                        updateMapping(index, 'transform', e.target.value || undefined)
                      }
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">None</option>
                      <option value="currency">Currency</option>
                      <option value="imageUrls">Image URLs</option>
                      <option value="uppercase">Uppercase</option>
                      <option value="trim">Trim Whitespace</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={mapping.required}
                      onChange={(e) => updateMapping(index, 'required', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleSaveConfig}
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
          >
            <CheckCircleIcon className="w-5 h-5 mr-2" />
            Save Configuration
          </button>

          <button
            onClick={handlePreviewSample}
            disabled={loading || !config.ftpHost}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
          >
            <DocumentTextIcon className="w-5 h-5 mr-2" />
            Preview Sample Data
          </button>

          <button
            onClick={handleManualImport}
            disabled={loading || !config.ftpHost}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center"
          >
            <CloudArrowUpIcon className="w-5 h-5 mr-2" />
            Select CSV File to Import
          </button>
        </div>
      </div>

      {/* Sample Data Preview */}
      {showSamplePreview && sampleData.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sample Data Preview</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  {Object.keys(sampleData[0]).map((key) => (
                    <th
                      key={key}
                      className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                    >
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sampleData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    {Object.values(row).map((val: any, i) => (
                      <td key={i} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                        {String(val).substring(0, 50)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarsForSaleImportConfig;
